import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownV3,
  type FilterItems,
  type MatchesItem,
  type RenderItemContent,
} from 'component-library';
import { useFlag } from 'core/lib/hooks/useFlag';
import { partnerTreesToListDropdown } from '../utils';
import { filterLocationItems, itemSearchFilter } from '../components/utils';
import { useInactiveState } from '../hooks/useLocationPicker/useInactiveState';
import { usePickerLabels } from '../hooks/usePickerLabels';
import {
  getLocationItemsByKey,
  manageOnLocationsChange,
  manageSelectedItems,
} from '../locationChangeHandler';
import { showStatusLabelOutsidePoland } from '../locationRow';
import { type LocationPickerProps } from '../types';
import {
  dropPartlySelectedParents,
  getDropdownValuesByLocation,
  getInitialValues,
  getLocationGroups,
  getShownSelection,
  toDropdownItems,
  toLocationValue,
  toLocationValues,
} from './dropdownItems';
import { LocationItemContent } from './LocationItemContent';
import { InactiveToggle } from './InactiveToggle';

const toValueArray = (value: string | string[] | null): string[] => {
  if (value === null) {
    return [];
  }

  return Array.isArray(value) ? value : [value];
};

export const LocationPickerDropdown = (props: LocationPickerProps) => {
  const { t } = useTranslation();
  const searchHierarchyEnabled = useFlag('location-picker-v2');
  const { hideInactiveLocations, showInactiveToggle } = useInactiveState();
  const { label, placeholder } = usePickerLabels(props);
  const multiSelect = !!props.multiSelect;
  const initialSelectionDoneRef = useRef(false);
  const lastLocationGroupsRef = useRef<string | null>(null);
  const {
    locations,
    permissions,
    allowSelectingBusinessPartners,
    includeBusinessAddress,
    selectedValues,
    autoSelect,
    onChange,
    onLocationGroupChange,
    showStatusLabelCallback = showStatusLabelOutsidePoland,
  } = props;

  const locationItems = useMemo(
    () =>
      partnerTreesToListDropdown({
        partners: locations,
        permissions,
        shouldExcludeInactiveValues: hideInactiveLocations,
        level: 0,
        allowSelectingBusinessPartners,
        includeBusinessAddress,
      }),
    [
      allowSelectingBusinessPartners,
      hideInactiveLocations,
      includeBusinessAddress,
      locations,
      permissions,
    ],
  );
  const locationItemsByKey = useMemo(() => getLocationItemsByKey(locationItems), [locationItems]);
  const dropdownItems = useMemo(
    () => toDropdownItems(locationItems, hideInactiveLocations),
    [hideInactiveLocations, locationItems],
  );
  const dropdownValuesByLocation = useMemo(
    () => getDropdownValuesByLocation(dropdownItems),
    [dropdownItems],
  );
  const selectedLocations = useMemo(
    () =>
      includeBusinessAddress
        ? manageSelectedItems(selectedValues, locationItemsByKey)
        : selectedValues,
    [includeBusinessAddress, locationItemsByKey, selectedValues],
  );
  const selectedDropdownValues = useMemo(
    () => selectedLocations.flatMap((location) => dropdownValuesByLocation.get(location) ?? []),
    [dropdownValuesByLocation, selectedLocations],
  );

  const selectLocations = useCallback(
    (locations: string[]) => {
      initialSelectionDoneRef.current = true;

      if (includeBusinessAddress) {
        manageOnLocationsChange(locations, onChange, locationItemsByKey);
      } else {
        onChange(locations);
      }
    },
    [includeBusinessAddress, locationItemsByKey, onChange],
  );

  useEffect(() => {
    if (dropdownItems.length === 0) {
      return;
    }

    const shownSelection = getShownSelection({
      selectedValues,
      items: dropdownItems,
      locationItemsByKey,
      includeBusinessAddress: !!includeBusinessAddress,
    });

    if (shownSelection.length < selectedValues.length) {
      onChange(shownSelection);

      return;
    }

    // Single select picks a parent on its own, without its children.
    if (!multiSelect) {
      return;
    }

    // DropdownV3 does this on the next click; doing it now keeps the trigger in step with the tree.
    const fullySelectedValues = dropPartlySelectedParents(dropdownItems, selectedDropdownValues);

    if (fullySelectedValues.length < selectedDropdownValues.length) {
      selectLocations(toLocationValues(fullySelectedValues, selectedDropdownValues));
    }
  }, [
    dropdownItems,
    includeBusinessAddress,
    locationItemsByKey,
    multiSelect,
    onChange,
    selectLocations,
    selectedDropdownValues,
    selectedValues,
  ]);

  useEffect(() => {
    if (
      !autoSelect ||
      initialSelectionDoneRef.current ||
      dropdownItems.length === 0 ||
      selectedValues.length > 0
    ) {
      return;
    }

    const initialValues = getInitialValues(dropdownItems, autoSelect, multiSelect);

    if (initialValues.length > 0) {
      selectLocations(initialValues);
    }
  }, [autoSelect, dropdownItems, multiSelect, selectLocations, selectedValues.length]);

  useEffect(() => {
    if (!onLocationGroupChange) {
      return;
    }

    const locationGroups = getLocationGroups(dropdownItems, selectedDropdownValues);
    const locationGroupsKey = JSON.stringify(locationGroups);

    if (locationGroupsKey === lastLocationGroupsRef.current) {
      return;
    }

    lastLocationGroupsRef.current = locationGroupsKey;
    onLocationGroupChange(locationGroups);
  }, [dropdownItems, onLocationGroupChange, selectedDropdownValues]);

  const filterItems = useCallback<FilterItems>(
    (_items, searchTerm) =>
      toDropdownItems(
        filterLocationItems(locationItems, searchTerm, multiSelect),
        hideInactiveLocations,
      ),
    [hideInactiveLocations, locationItems, multiSelect],
  );

  const matchesItem = useCallback<MatchesItem>(
    (item, searchTerm) => {
      const locationItem = locationItemsByKey[toLocationValue(item.value)];

      return itemSearchFilter(locationItem, searchTerm.toLowerCase(), true);
    },
    [locationItemsByKey],
  );

  const renderItemContent = useCallback<RenderItemContent>(
    (dropdownItem) => {
      // Every row, search results included, comes from locationItems.
      const item = locationItemsByKey[toLocationValue(dropdownItem.value)];

      return (
        <LocationItemContent
          item={item}
          label={dropdownItem.label}
          multiSelect={multiSelect}
          hideInactiveLocations={hideInactiveLocations}
          showStatusLabel={showStatusLabelCallback(item)}
        />
      );
    },
    [hideInactiveLocations, locationItemsByKey, multiSelect, showStatusLabelCallback],
  );

  return (
    <DropdownV3
      data-testid="location-picker"
      items={dropdownItems}
      multiple={multiSelect}
      expandable={true}
      fullWidth={true}
      countSelectedGroups={true}
      selectedValuesDisplay="list"
      selectedValues={selectedDropdownValues}
      onChange={(value) => {
        selectLocations(toLocationValues(toValueArray(value), selectedDropdownValues));
      }}
      loading={props.loading}
      disabled={props.disabled}
      required={props.required}
      error={props.error}
      errorMessage={props.errorMessage}
      description={props.description}
      filterItems={searchHierarchyEnabled ? undefined : filterItems}
      matchesItem={searchHierarchyEnabled ? matchesItem : undefined}
      hideSearchParents={searchHierarchyEnabled}
      renderItemContent={renderItemContent}
      headerContent={showInactiveToggle ? <InactiveToggle /> : undefined}
      label={label}
      triggerPlaceholder={placeholder}
      searchPlaceholder={t('filter.searchLocation')}
      searchNoResultsText={(searchQuery) =>
        t('components.dropdown.search.location.result.empty', { searchQuery })
      }
      searchClearLabel={t('filter.clearSearch')}
      selectAllLabel={t('components.dropdown.select.all')}
      allSelectedLabel={t('components.dropdown.all')}
    />
  );
};
